import React from 'react';
import { Link } from 'react-router-dom';
import { Pin, Trash2, Heart, MessageCircle, ChevronRight, Lock } from 'lucide-react';
import { cn, getDiscussionPath } from '../utils';

const DiscussionCard = ({ 
  discussion, 
  isAdmin = false, 
  onDelete = null, 
  onVote = null,
  compact = false 
}) => {
  if (!discussion) return null;

  const {
    id,
    title,
    content,
    author_name,
    author,
    created_at,
    likes,
    comments_count,
    is_pinned,
    privacy,
    settings,
    has_password
  } = discussion;

  const isPrivate = privacy === 'private' || settings?.privacy === 'private' || has_password || settings?.password;
  const authorName = author_name || author?.name || (typeof author === 'string' ? author : 'Anonymous');
  const dateStr = new Date(created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  if (compact) {
    return (
      <Link 
        to={getDiscussionPath(discussion)} 
        className="block bg-white p-6 rounded-3xl border border-gray-100 hover:border-brand-indigo/20 hover:shadow-2xl hover:shadow-brand-indigo/5 cursor-pointer transition-all active:scale-[0.98] group shadow-sm"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-3">
              <h4 className="font-bold text-gray-900 group-hover:text-brand-indigo transition-colors line-clamp-1 text-lg leading-tight">{title}</h4>
              {isPrivate && <Lock size={12} className="text-gray-300 shrink-0" />}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2">
                {[1, 2, 3].map(j => (
                  <div key={j} className="w-6 h-6 rounded-full border-2 border-white bg-gray-50 overflow-hidden shadow-sm">
                    <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${j + (authorName)}`} alt="avatar" />
                  </div>
                ))}
              </div>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">{comments_count || 0} replies</span>
              <span className="w-1 h-1 bg-gray-200 rounded-full"></span>
              <span className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">{dateStr}</span>
            </div>
          </div>
          <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-gray-300 group-hover:bg-brand-indigo/5 group-hover:text-brand-indigo transition-all">
            <ChevronRight size={20} />
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="relative group">
      <Link 
        to={getDiscussionPath(discussion)} 
        className="bg-white p-6 rounded-3xl border border-gray-100 hover:border-brand-indigo/10 transition-all active:scale-[0.99] cursor-pointer shadow-sm hover:shadow-2xl hover:shadow-brand-indigo/5 block relative z-0"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-100 overflow-hidden shadow-inner">
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${authorName}`} alt={authorName} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-bold text-gray-900 group-hover:text-brand-indigo transition-colors leading-tight">{title}</h3>
                {is_pinned && <Pin size={14} className="text-brand-indigo fill-brand-indigo shrink-0" />}
                {isPrivate && <Lock size={12} className="text-gray-300 shrink-0" />}
              </div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.15em]">
                {authorName} • {dateStr}
              </p>
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500 font-medium line-clamp-2 mb-6 leading-relaxed">
          {content}
        </p>
        <div className="flex items-center gap-4">
          <button 
            onClick={(e) => {
              if (onVote) {
                e.preventDefault();
                e.stopPropagation();
                onVote(id);
              }
            }}
            className="flex items-center gap-1.5 text-gray-400 text-[10px] font-bold uppercase tracking-[0.15em] bg-gray-50 px-3 py-1.5 rounded-xl hover:text-red-500 hover:bg-red-50 transition-all active:scale-95"
          >
            <Heart size={12} className="group-hover:text-red-500 transition-colors" />
            {likes || 0}
          </button>
          <div className="flex items-center gap-1.5 text-gray-400 text-[10px] font-bold uppercase tracking-[0.15em] bg-gray-50 px-3 py-1.5 rounded-xl">
            <MessageCircle size={12} className="group-hover:text-brand-indigo transition-colors" />
            {comments_count || 0}
          </div>
        </div>
      </Link>
      {isAdmin && onDelete && (
        <button 
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete(id);
          }}
          className="absolute top-6 right-6 p-2.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all opacity-0 group-hover:opacity-100 z-10 active:scale-90"
        >
          <Trash2 size={16} />
        </button>
      )}
    </div>
  );
};

export default DiscussionCard;
